import api from './api';

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  username?: string; // Optionnel
  first_name: string;
  last_name: string;
  password: string;
  re_password: string;
  role?: string;
  phone?: string;
  company_name?: string;
}

export interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  phone?: string;
  company_name?: string;
  full_name: string;
  is_active: boolean;
  date_joined: string;
}

export interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
}

class AuthService {
  // Connexion utilisateur
  async login(data: LoginData): Promise<AuthResponse> {
    try {
      // Djoser JWT attend 'username' et 'password', mais notre modèle utilise email
      const loginPayload = {
        username: data.email, // Utiliser email comme username
        password: data.password
      };
      
      console.log('Tentative de connexion avec:', { email: data.email, username: loginPayload.username });
      
      const response = await api.post('/auth/jwt/create/', loginPayload);
      const { access, refresh } = response.data;
      
      // Stocker les tokens
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      
      // Récupérer les informations utilisateur
      const userResponse = await api.get('/auth/users/me/');
      
      return {
        access,
        refresh,
        user: userResponse.data
      };
    } catch (error: any) {
      console.error('Erreur de connexion détaillée:', error.response?.data);
      console.error('Status:', error.response?.status);
      console.error('Headers:', error.response?.headers);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.non_field_errors?.[0] ||
                          error.response?.data?.message ||
                          'Erreur de connexion';
      throw new Error(errorMessage);
    }
  }

  // Inscription utilisateur
  async register(data: RegisterData): Promise<User> {
    try {
      const response = await api.post('/auth/register/', data);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data;
      if (errorMessage) {
        // Gérer les erreurs spécifiques
        if (errorMessage.email) {
          throw new Error(`Email: ${errorMessage.email[0]}`);
        }
        if (errorMessage.username) {
          throw new Error(`Nom d'utilisateur: ${errorMessage.username[0]}`);
        }
        if (errorMessage.password) {
          throw new Error(`Mot de passe: ${errorMessage.password[0]}`);
        }
        if (errorMessage.non_field_errors) {
          throw new Error(errorMessage.non_field_errors[0]);
        }
      }
      throw new Error('Erreur lors de l\'inscription');
    }
  }

  // Déconnexion
  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // Vérifier si l'utilisateur est connecté
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Récupérer les informations de l'utilisateur connecté
  async getCurrentUser(): Promise<User> {
    try {
      const response = await api.get('/auth/users/me/');
      return response.data;
    } catch (error) {
      throw new Error('Impossible de récupérer les informations utilisateur');
    }
  }

  // Récupérer le token d'accès
  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Récupérer le token de rafraîchissement
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  // Mettre à jour le profil utilisateur
  async updateProfile(userData: Partial<RegisterData>): Promise<User> {
    try {
      const response = await api.patch('/auth/users/me/', userData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      const errorMessage = error.response?.data;
      
      if (errorMessage) {
        // Gérer les erreurs spécifiques
        if (errorMessage.email) {
          throw new Error(`Email: ${errorMessage.email[0]}`);
        }
        if (errorMessage.phone) {
          throw new Error(`Téléphone: ${errorMessage.phone[0]}`);
        }
        if (errorMessage.non_field_errors) {
          throw new Error(errorMessage.non_field_errors[0]);
        }
      }
      
      throw new Error('Erreur lors de la mise à jour du profil');
    }
  }
}

export default new AuthService();
